import { logSwallowed } from '@/lib/errors';
// ============================================================================
// fileSync.ts — синк ФАЙЛОВ (offline-first, раздел 5 плана).
// Содержимое файлов (file_blobs) синкается ОТДЕЛЬНО от метаданных:
//   - сервер отдаёт список {id, file_hash (SHA-256), file_size} — ListFileHashes
//   - клиент качает ТОЛЬКО отсутствующие/изменённые (hash-индекс в IndexedDB)
//   - blob'ы хранятся в IndexedDB (idb kv: 'file_blob:<id>'), кэш — 'file_cache:<id>'
//   - файлы больше лимита — только по запросу (on-demand)
// Offline-first: файл не загружен → «файл не загружен (оффлайн)» (НЕ фоллбэк).
// SSoT — сервер; здесь только persisted кэш содержимого.
// ============================================================================

import { idbGet, idbSet } from '@/lib/storage/idb';
import { opfsGet, opfsSet, opfsDelete } from '@/lib/storage/opfs';
import { useAuthStore } from '@/store/authStore';
import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';
import { useCenterStore } from '@/store/centerStore';

const log = createLogger('FileSync');

// Лимит авто-синка содержимого (крупные файлы — только по запросу).
// Настройка sync.max_file_size — будущая фича (сейчас константа).
export const FILE_SYNC_MAX_SIZE = 10 * 1024 * 1024; // 10 МБ

// Блобы ≥ порога хранятся в OPFS (крупные медиа/ассеты — там эффективнее),
// меньшие — в IndexedDB (быстро, доступны Service Worker'у). OPFS недоступен
// → всё в IndexedDB.
export const FILE_OPFS_THRESHOLD = 2 * 1024 * 1024; // 2 МБ

interface FileCacheEntry {
  hash: string;
  size: number;
  ts: number;
}

function blobKey(id: string): string {
  return `file_blob:${id}`;
}

function cacheKey(id: string): string {
  return `file_cache:${id}`;
}

/** BlobPart из произвольного Uint8Array (TS 5.7: ArrayBufferLike → ArrayBuffer):
 *  копия в НОВЫЙ ArrayBuffer — типобезопасно и без мутаций исходного. */
function toBlobPart(bytes: Uint8Array): BlobPart {
  const buf = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buf).set(bytes);
  return buf;
}

/** Хэш-индекс файла в кэше (null — не загружен). */
export async function getFileCache(id: string): Promise<FileCacheEntry | null> {
  try {
    return (await idbGet<FileCacheEntry>(cacheKey(id))) || null;
  } catch {
    return null;
  }
}

/** Содержимое файла из кэша (null — не загружен). Крупные — в OPFS, иначе IndexedDB. */
export async function getFileBlob(id: string): Promise<Blob | null> {
  try {
    const fromIdb = (await idbGet<Blob>(blobKey(id))) || null;
    if (fromIdb) return fromIdb;
  } catch { /* IDB недоступен — пробуем OPFS */ }
  try {
    return await opfsGet(id);
  } catch {
    return null;
  }
}

/** Запись блоба в кэш: крупные (≥ порога) — OPFS, иначе IndexedDB. Убирает
 *  дубль из противоположного хранилища (при смене размера/хранилища). */
async function putFileBlob(id: string, blob: Blob): Promise<void> {
  if (blob.size >= FILE_OPFS_THRESHOLD) {
    const ok = await opfsSet(id, blob);
    if (ok) {
      try { await idbSet(blobKey(id), null); } catch { /* не критично */ }
      return;
    }
  }
  await idbSet(blobKey(id), blob);
  void opfsDelete(id);
}

/** Удалить файл из кэша (удаление/скрытие на сервере) — оба хранилища. */
export async function removeFileFromCache(id: string): Promise<void> {
  try {
    await idbSet(blobKey(id), null);
    await idbSet(cacheKey(id), null);
  } catch { /* не критично */ }
  await opfsDelete(id);
}

/**
 * Синк содержимого: для каждого файла с сервера сверяем hash с кэшем,
 * отсутствующие/изменённые (в пределах лимита) — качаем. Возвращает
 * количество скачанных файлов. Детерминированно: ошибки БРОСАЮТСЯ.
 */
export async function syncFiles(): Promise<number> {
  // Только авторизованному (listFileHashes требует токен — до входа 401-шум)
  if (!useAuthStore.getState().isAuthenticated) return 0;
  const sdk = getSDK();
  const resp = await sdk.files.listFileHashes({});
  const items = ((resp as any)?.items || []) as Array<{ id: string; fileHash?: string; fileSize?: number }>;
  let downloaded = 0;
  for (const item of items) {
    const id = String(item.id || '');
    const hash = String(item.fileHash || '');
    const size = Number(item.fileSize ?? 0);
    if (!id || !hash) continue;
    if (size > FILE_SYNC_MAX_SIZE) continue; // крупные — только по запросу
    const cached = await getFileCache(id);
    if (cached && cached.hash === hash) continue;
    // Нет в кэше или изменился — качаем и сохраняем
    try {
      const file = await sdk.files.downloadFile({ id });
      const bytes = (file as any)?.data as Uint8Array | undefined;
      if (!bytes) continue;
      const blob = new Blob([toBlobPart(bytes)], { type: (file as any)?.fileType || 'application/octet-stream' });
      await putFileBlob(id, blob);
      await idbSet(cacheKey(id), { hash, size, ts: Date.now() } satisfies FileCacheEntry);
      downloaded++;
    } catch (e) {
      // Один файл не скачался — остальные продолжаем, но факт фиксируем
      log.warn('sync file failed', { id, error: e });
    }
  }
  return downloaded;
}

/**
 * Получение содержимого файла ДЛЯ ПОЛЬЗОВАТЕЛЯ (кнопка «Скачать»):
 * 1) blob из кэша (оффлайн-доступ); 2) иначе — с сервера (on-demand, с сохранением
 * в кэш). Вернёт {blob, fileName, fileType} — или выбросит (нет сети, нет кэша).
 */
export async function ensureFileBlob(id: string): Promise<{ blob: Blob; fileName: string; fileType: string }> {
  // Оффлайн: только кэш
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    const cached = await getFileBlob(id);
    if (cached) {
      return { blob: cached, fileName: 'file', fileType: cached.type };
    }
    throw new Error('Файл не загружен (оффлайн) — подключитесь к сети и нажмите «Загрузить»');
  }
  const resp = await getSDK().files.downloadFile({ id });
  const bytes = (resp as any)?.data as Uint8Array | undefined;
  if (!bytes) throw new Error('Файл пуст или недоступен');
  const fileName = String((resp as any)?.fileName || 'file');
  const fileType = String((resp as any)?.fileType || 'application/octet-stream');
  const blob = new Blob([toBlobPart(bytes)], { type: fileType });
  // Сохраняем в кэш (для оффлайн) — hash из сервера неизвестен здесь,
  // поэтому кэш помечаем запрошенным (hash='' — при следующем синке перекачается)
  try {
    await putFileBlob(id, blob);
    const existing = await getFileCache(id);
    await idbSet(cacheKey(id), { hash: existing?.hash || '', size: existing?.size || bytes.length, ts: Date.now() });
  } catch (e) { logSwallowed(e, 'lib/fileSync.ts:160'); }
  return { blob, fileName, fileType };
}

// ============================================================================
// ЧАНКОВОЕ СКАЧИВАНИЕ (прогресс, пауза/возобновление, отмена; offline-first)
// GET /files/{id} (HTTP, Range поддержка на сервере — http.ServeContent 206).
// Пауза/возобновление: уже скачанные байты сохраняются в IndexedDB-куски,
// докачка — Range: bytes=<done>- (идемпотентно, переживает перезагрузку).
// ============================================================================

export const DOWNLOAD_CHUNK_SIZE = 4 * 1024 * 1024; // 4 МБ на «порцию» прогресса

export type DownloadStatus = 'downloading' | 'paused' | 'done' | 'error' | 'aborted';

export interface DownloadJob {
  id: string;             // file_id
  fileName: string;
  status: DownloadStatus;
  progress: number;       // 0..1
  downloadedBytes: number;
  totalBytes: number;
  error?: string;
}

// Активные скачивания (несколько одновременно) — SSoT процесса
const downloads = new Map<string, DownloadJob>();
type DownloadListener = () => void;
const dlListeners = new Set<DownloadListener>();
export function onDownloadsChange(fn: DownloadListener): () => void {
  dlListeners.add(fn);
  return () => dlListeners.delete(fn);
}
function dlEmit() { for (const fn of dlListeners) fn(); }

export function getDownloadJobs(): DownloadJob[] {
  return Array.from(downloads.values());
}

// Кусок файла в IndexedDB (для докачки)
function dlPartKey(id: string, part: number): string {
  return `file_dl:${id}:${part}`;
}
// Прогресс-индекс (сколько частей сохранено)
function dlMetaKey(id: string): string {
  return `file_dlmeta:${id}`;
}
interface DlMeta { parts: number; partSize: number; fileName: string }

async function savedParts(id: string): Promise<{ meta: DlMeta | null; parts: Map<number, Blob> }> {
  try {
    const meta = await idbGet<DlMeta>(dlMetaKey(id));
    if (!meta) return { meta: null, parts: new Map() };
    const parts = new Map<number, Blob>();
    for (let i = 0; i < meta.parts; i++) {
      const b = await idbGet<Blob>(dlPartKey(id, i));
      if (b) parts.set(i, b);
    }
    return { meta, parts };
  } catch {
    return { meta: null, parts: new Map() };
  }
}

/**
 * Скачивание файла чанками с прогрессом/паузой/отменой/возобновлением.
 * Возвращает полный Blob (после завершения — в кэше fileSync тоже).
 */
export async function downloadFileWithProgress(id: string): Promise<{ blob: Blob; fileName: string }> {
  const token = useAuthStore.getState().token || useCenterStore.getState().token;
  if (!token) throw new Error('Не авторизован');

  const { meta, parts } = await savedParts(id);
  const partSize = meta?.partSize || DOWNLOAD_CHUNK_SIZE;
  const fileName = meta?.fileName || 'file';

  // Считаем уже скачанные байты (по частям, без дыр)
  let doneBytes = 0;
  let doneParts = 0;
  if (meta) {
    for (let i = 0; i < meta.parts; i++) {
      if (parts.has(i)) { doneBytes += partSize; doneParts++; }
      else break;
    }
  }

  const job: DownloadJob = {
    id, fileName, status: 'downloading', progress: 0, downloadedBytes: doneBytes, totalBytes: 0,
  };
  downloads.set(id, job);
  dlEmit();

  const ctrl = new AbortController();
  const jobRef = { ctrl };

  try {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (doneBytes > 0) headers.Range = `bytes=${doneBytes}-`;
    const resp = await fetch(`/files/${encodeURIComponent(id)}`, { headers, signal: ctrl.signal });
    if (resp.status === 401 || resp.status === 403) throw new Error('Нет доступа к файлу');
    if (!resp.ok && resp.status !== 206) throw new Error(`Ошибка скачивания (HTTP ${resp.status})`);

    // Фактическое имя файла — из Content-Disposition сервера (например setup.exe).
    // Если скачивание продолжается (докачка) — берём сохранённое в метаданных.
    let resolvedFileName = fileName;
    if (resolvedFileName === 'file' || resolvedFileName === '') {
      const cd = resp.headers.get('content-disposition') || '';
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      if (m?.[1]) {
        try { resolvedFileName = decodeURIComponent(m[1]); } catch { resolvedFileName = m[1]; }
      }
    }
    job.fileName = resolvedFileName;

    const total = resp.status === 206
      ? doneBytes + Number(resp.headers.get('content-range')?.split('/')[1] || 0)
      : Number(resp.headers.get('content-length') || 0) + doneBytes;
    job.totalBytes = total;
    job.progress = total > 0 ? doneBytes / total : 0;
    dlEmit();

    const reader = resp.body?.getReader();
    if (!reader) throw new Error('Нет потока данных');
    const chunks: Uint8Array[] = [];
    let partBuf = new Uint8Array(0);
    while (true) {
      if (job.status === 'aborted') { ctrl.abort(); throw new Error('Скачивание отменено'); }
      if (job.status === 'paused') {
        // Пауза: сохраняем прогресс (уже сделанные части в parts) — продолжение
        // создаст новый fetch с Range: bytes=<doneBytes>-
        break;
      }
      const { value, done } = await reader.read();
      if (done) break;
      chunks.push(value);
      // накапливаем в «части» (для докачки) и прогресс
      partBuf = concatUint8(partBuf, value);
      while (partBuf.length >= partSize) {
        const part = partBuf.slice(0, partSize);
        partBuf = partBuf.slice(partSize);
        await idbSet(dlPartKey(id, doneParts), new Blob([part as unknown as BlobPart], { type: 'application/octet-stream' }));
        doneParts++;
        doneBytes += partSize;
        await idbSet(dlMetaKey(id), { parts: doneParts, partSize, fileName: resolvedFileName } satisfies DlMeta);
      }
      job.downloadedBytes = doneBytes + partBuf.length;
      job.progress = total > 0 ? Math.min(1, job.downloadedBytes / total) : 0;
      dlEmit();
    }

    if (job.status === 'paused') {
      // сохраняем неполную часть (для докачки) — начиная с неё
      if (partBuf.length > 0) {
        await idbSet(dlPartKey(id, doneParts), new Blob([partBuf as unknown as BlobPart], { type: 'application/octet-stream' }));
        doneParts++;
        await idbSet(dlMetaKey(id), { parts: doneParts, partSize, fileName: resolvedFileName } satisfies DlMeta);
      }
      dlEmit();
      // Бросаем специальную ошибку — вызывающий знает, что это пауза
      const err = new Error('paused') as Error & { paused?: boolean };
      err.paused = true;
      throw err;
    }

    // Собираем итоговый Blob из частей + остатка
    const allParts: Blob[] = [];
    const { parts: saved } = await savedParts(id);
    for (let i = 0; i < doneParts; i++) {
      const b = saved.get(i);
      if (b) allParts.push(b);
    }
    if (partBuf.length > 0) allParts.push(new Blob([partBuf as unknown as BlobPart], { type: 'application/octet-stream' }));
    const full = new Blob(allParts, { type: 'application/octet-stream' });

    // Кэш fileSync (для оффлайн)
    try {
      await putFileBlob(id, full);
      const existing = await getFileCache(id);
      await idbSet(cacheKey(id), { hash: existing?.hash || '', size: existing?.size || full.size, ts: Date.now() });
    } catch (e) { logSwallowed(e, 'lib/fileSync.ts:339'); }
    // Чистим временные части
    for (let i = 0; i <= doneParts; i++) { try { await idbSet(dlPartKey(id, i), null); } catch (e) { logSwallowed(e, 'lib/fileSync.ts:341'); } }
    try { await idbSet(dlMetaKey(id), null); } catch (e) { logSwallowed(e, 'lib/fileSync.ts:342'); }

    job.status = 'done';
    job.progress = 1;
    job.downloadedBytes = full.size;
    job.totalBytes = full.size;
    dlEmit();
    return { blob: full, fileName: resolvedFileName };
  } catch (e: any) {
    if (e?.paused) throw e; // пауза — не ошибка
    if (job.status === 'aborted') {
      // отмена: чистим временные части
      const { meta: m } = await savedParts(id);
      if (m) for (let i = 0; i <= m.parts; i++) { try { await idbSet(dlPartKey(id, i), null); } catch (e) { logSwallowed(e, 'lib/fileSync.ts:355'); } }
      try { await idbSet(dlMetaKey(id), null); } catch (e) { logSwallowed(e, 'lib/fileSync.ts:356'); }
      job.status = 'aborted';
      dlEmit();
      throw new Error('Скачивание отменено');
    }
    job.status = 'error';
    job.error = e?.message || 'Ошибка скачивания';
    dlEmit();
    throw e;
  }
}

/** Пауза скачивания (продолжение — downloadFileWithProgress снова, Range-докачка). */
export function pauseDownload(id: string) {
  const j = downloads.get(id);
  if (j && j.status === 'downloading') {
    j.status = 'paused';
    dlEmit();
  }
}

/** Отмена скачивания (чистит временные части). */
export function abortDownload(id: string) {
  const j = downloads.get(id);
  if (!j) return;
  j.status = 'aborted';
  dlEmit();
}

function concatUint8(a: Uint8Array, b: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}
